import JSONBigInt from "json-bigint";
import { AxiosRequestConfig } from "axios";
import { RequestManager } from "./index";

const baseUrl = process.env.BIG_FLIP_GATEWAY_BASE_URL;

// Parse JSON with big integers as strings to preserve precision for large IDs
const JSONBigIntParser = JSONBigInt({ storeAsString: true });

/**
 * json-bigint builds objects with `Object.create(null)`, so parsed objects have no
 * prototype (no `hasOwnProperty`, etc). That diverges from the default axios `JSON.parse`
 * behaviour and breaks any consumer that relies on prototype methods. Rebuild the tree with
 * plain objects so gateway responses behave identically to legacy responses.
 * Big integers are already stored as strings here, so they are preserved untouched.
 */
const normalizePrototype = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(normalizePrototype);
  }
  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc: Record<string, any>, key) => {
      acc[key] = normalizePrototype(value[key]);
      return acc;
    }, {});
  }
  return value;
};

/**
 * Custom transform response function that uses json-bigint
 * This preserves large integer IDs that exceed JavaScript's Number.MAX_SAFE_INTEGER
 */
const bigIntTransformResponse = (data: any) => {
  if (typeof data === "string") {
    try {
      return normalizePrototype(JSONBigIntParser.parse(data));
    } catch {
      return data;
    }
  }
  return data;
};

/**
 * Extended RequestManager for Gateway that preserves big integer IDs
 */
export class GatewayRequestManager extends RequestManager {
  async get<Response = any>(
    endpoint: string,
    params: Record<string, any> = {},
    customHeaders: Record<string, any> = {},
    additionalConfig: AxiosRequestConfig = {}
  ): Promise<Response> {
    return super.get<Response>(endpoint, params, customHeaders, {
      transformResponse: [bigIntTransformResponse],
      ...additionalConfig
    });
  }

  async post<Response = any>(
    endpoint: string,
    data: any = {},
    customHeaders: Record<string, any> = {},
    additionalConfig: AxiosRequestConfig = {}
  ): Promise<Response> {
    return super.post<Response>(endpoint, data, customHeaders, {
      transformResponse: [bigIntTransformResponse],
      ...additionalConfig
    });
  }

  async put<Response = any>(
    endpoint: string,
    data: any = {},
    customHeaders: Record<string, any> = {},
    additionalConfig: AxiosRequestConfig = {}
  ): Promise<Response> {
    return super.put<Response>(endpoint, data, customHeaders, {
      transformResponse: [bigIntTransformResponse],
      ...additionalConfig
    });
  }
  async delete<Response = any>(
    endpoint: string,
    params: Record<string, any> = {},
    customHeaders: Record<string, any> = {},
    additionalConfig: AxiosRequestConfig = {}
  ): Promise<Response> {
    return super.delete<Response>(endpoint, params, customHeaders, {
      transformResponse: [bigIntTransformResponse],
      ...additionalConfig
    });
  }
}

const request = new GatewayRequestManager(baseUrl);
export default request;
